import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBanners } from '../context/BannerContext';
import { useUserProfile } from '../context/UserProfileContext';
import { createElement } from 'react';
import { Clock } from 'lucide-react';

const BANNER_ID = 'trial-expiring';
const TRIAL_WARNING_DAYS = 10;

function daysUntil(unixSeconds: number): number {
  return Math.max(0, Math.ceil((unixSeconds * 1000 - Date.now()) / (1000 * 60 * 60 * 24)));
}

/**
 * Shows a banner when the org's trial period has 10 or fewer days remaining.
 * Admins get a link to Manage Plan; non-admins are prompted to contact their admin.
 */
export function useTrialBanner() {
  const { trialEnd, activeOrg } = useUserProfile();
  const { addBanner, removeBanner } = useBanners();
  const navigate = useNavigate();

  const isAdmin = activeOrg?.role === 'admin';
  const remaining = trialEnd ? daysUntil(trialEnd) : null;
  const showBanner = remaining !== null && remaining <= TRIAL_WARNING_DAYS;

  useEffect(() => {
    if (!showBanner || remaining === null) {
      removeBanner(BANNER_ID);
      return;
    }

    addBanner({
      id: BANNER_ID,
      priority: 8,
      content: createElement(TrialBanner, {
        daysLeft: remaining,
        isAdmin,
        onManagePlan: () => navigate('/admin/company/billing/plan'),
      }),
    });

    return () => removeBanner(BANNER_ID);
  }, [showBanner, remaining, isAdmin, addBanner, removeBanner, navigate]);
}

function TrialBanner({
  daysLeft,
  isAdmin,
  onManagePlan,
}: {
  daysLeft: number;
  isAdmin: boolean;
  onManagePlan: () => void;
}) {
  const urgent = daysLeft <= 3;
  const bgClass = urgent
    ? 'bg-red-50 border-b border-red-200'
    : 'bg-amber-50 border-b border-amber-200';
  const iconColor = urgent ? 'text-red-600' : 'text-amber-600';
  const textColor = urgent ? 'text-red-800' : 'text-amber-800';

  const label =
    daysLeft === 0
      ? 'Trial expires today'
      : daysLeft === 1
        ? 'Trial expires tomorrow'
        : `Trial expires in ${daysLeft} days`;

  return createElement(
    'div',
    { className: `${bgClass} px-4 py-2 flex items-center justify-between gap-3` },
    // Left: icon + label
    createElement(
      'div',
      { className: 'flex items-center gap-2 min-w-0' },
      createElement(Clock, { size: 16, className: `shrink-0 ${iconColor}` }),
      createElement(
        'p',
        { className: `text-small font-semibold ${textColor} truncate` },
        label,
      ),
    ),
    // Right: CTA
    isAdmin
      ? createElement(
          'button',
          {
            type: 'button',
            onClick: onManagePlan,
            className:
              'shrink-0 text-small font-semibold text-plum hover:text-rose transition-colors whitespace-nowrap',
          },
          'Manage Plan',
        )
      : createElement(
          'span',
          { className: 'shrink-0 text-small text-slate whitespace-nowrap' },
          'Contact your Admin',
        ),
  );
}
